<template>
  <div class="acc-view">
    <header class="acc-topbar">
      <span class="acc-topbar__section">Mi cuenta</span>
      <div class="acc-topbar__spacer"></div>
      <div v-if="user" class="acc-topbar__avatar" :style="{ backgroundColor: user.avatarColor }">
        <img v-if="user.avatarUrl" :src="avatarImg" class="acc-avatar-img" alt="" />
        <template v-else>{{ initials }}</template>
      </div>
    </header>

    <div v-if="!user" class="acc-loading">
      <span class="mdi mdi-loading acc-loading__spinner"></span>
    </div>

    <div v-else class="acc-body">
      <!-- Columna izquierda — identidad visual -->
      <aside class="acc-identity">
        <div class="acc-identity__avatar" :style="{ backgroundColor: user.avatarColor }">
          <img v-if="user.avatarUrl" :src="avatarImg" class="acc-avatar-img" alt="" />
          <template v-else>{{ initials }}</template>
        </div>
        <input ref="avatarInput" type="file" accept="image/jpeg,image/png,image/webp" class="acc-hidden" @change="onAvatarSelected" />
        <div class="acc-identity__photo-actions">
          <button class="acc-photo-link" :disabled="uploadingAvatar" @click="avatarInput?.click()">
            {{ user.avatarUrl ? 'Cambiar foto' : 'Subir foto' }}
          </button>
          <button v-if="user.avatarUrl" class="acc-photo-link acc-photo-link--danger" :disabled="uploadingAvatar" @click="onRemoveAvatar">
            Quitar
          </button>
        </div>
        <div v-if="avatarError" class="acc-error acc-error--center">{{ avatarError }}</div>
        <div class="acc-identity__name">{{ user.name || user.email }}</div>
        <div class="acc-identity__role" :style="{ color: roleInfo.color }">{{ roleInfo.label }}</div>
        <div class="acc-identity__park">
          <span class="mdi mdi-office-building-outline"></span>
          {{ user.parkName }}
        </div>
        <div class="acc-identity__since">Miembro desde {{ formatDate(user.activatedAt) }}</div>
      </aside>

      <!-- Columna derecha — secciones -->
      <main class="acc-content">
        <!-- Sección 1 — Datos personales -->
        <section class="acc-section">
          <div class="acc-section__label">Datos personales</div>

          <div class="acc-grid">
            <div class="acc-col">
              <label class="acc-field">
                <span class="acc-field__label">Nombre completo</span>
                <input v-model="profileForm.name" type="text" class="acc-input" />
              </label>

              <label class="acc-field">
                <span class="acc-field__label">
                  Correo electrónico
                  <span class="acc-badge acc-badge--ok">Verificado</span>
                </span>
                <input :value="user.email" type="email" class="acc-input acc-input--readonly" readonly />
                <span class="acc-field__hint">El cambio de correo estará disponible próximamente.</span>
              </label>
            </div>

            <div class="acc-col">
              <label class="acc-field">
                <span class="acc-field__label">Teléfono de contacto (opcional)</span>
                <div class="acc-phone">
                  <span class="acc-phone__prefix">+52</span>
                  <input
                    :value="formattedPhone"
                    type="tel"
                    class="acc-input acc-phone__input"
                    placeholder="(55) 1234-5678"
                    @input="onPhoneInput"
                  />
                </div>
              </label>

              <label class="acc-field">
                <span class="acc-field__label">Cargo o puesto en el parque</span>
                <input v-model="profileForm.jobTitle" type="text" class="acc-input" placeholder="Ej. Gerente de operaciones" />
              </label>
            </div>
          </div>

          <div v-if="profileError" class="acc-error">{{ profileError }}</div>
          <button class="acc-btn" :disabled="!profileDirty || savingProfile" @click="onSaveProfile">
            Guardar datos personales
          </button>
        </section>

        <!-- Sección 2 — Seguridad -->
        <section class="acc-section">
          <div class="acc-section__label">Seguridad</div>

          <div class="acc-password">
            <label class="acc-field">
              <span class="acc-field__label">Contraseña actual</span>
              <div class="acc-input-wrap">
                <input
                  v-model="pwForm.current"
                  :type="show.current ? 'text' : 'password'"
                  class="acc-input"
                  autocomplete="current-password"
                />
                <button type="button" class="acc-eye" tabindex="-1" @click="show.current = !show.current">
                  <span class="mdi" :class="show.current ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"></span>
                </button>
              </div>
            </label>

            <label class="acc-field">
              <span class="acc-field__label">Nueva contraseña</span>
              <div class="acc-input-wrap">
                <input
                  v-model="pwForm.next"
                  :type="show.next ? 'text' : 'password'"
                  class="acc-input"
                  autocomplete="new-password"
                />
                <button type="button" class="acc-eye" tabindex="-1" @click="show.next = !show.next">
                  <span class="mdi" :class="show.next ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"></span>
                </button>
              </div>
              <div v-if="pwForm.next" class="acc-strength">
                <div class="acc-strength__bar">
                  <div
                    class="acc-strength__fill"
                    :style="{ width: strength.percent + '%', backgroundColor: strength.color }"
                  ></div>
                </div>
                <span class="acc-strength__label" :style="{ color: strength.color }">{{ strength.label }}</span>
              </div>
            </label>

            <label class="acc-field">
              <span class="acc-field__label">Confirmar nueva contraseña</span>
              <div class="acc-input-wrap">
                <input
                  v-model="pwForm.confirm"
                  :type="show.confirm ? 'text' : 'password'"
                  class="acc-input"
                  autocomplete="new-password"
                />
                <span v-if="pwMatches" class="mdi mdi-check-circle acc-match"></span>
                <button type="button" class="acc-eye" tabindex="-1" @click="show.confirm = !show.confirm">
                  <span class="mdi" :class="show.confirm ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"></span>
                </button>
              </div>
            </label>

            <div class="acc-note">La contraseña debe tener al menos 8 caracteres, una mayúscula, y un número.</div>
          </div>

          <div v-if="pwError" class="acc-error">{{ pwError }}</div>
          <button class="acc-btn" :disabled="!canChangePassword || savingPassword" @click="onChangePassword">
            Cambiar contraseña
          </button>

          <div class="acc-subsection">
            <div class="acc-subsection__label">Accesos recientes</div>

            <ul v-if="history.length" class="acc-history">
              <li v-for="item in history" :key="item.id" class="acc-history__row">
                <span class="mdi acc-history__icon" :class="deviceIcon(item.deviceType)"></span>
                <span class="acc-history__device">{{ deviceLabel(item.deviceType) }} · {{ item.browser }}</span>
                <span class="acc-history__date">{{ formatDateTime(item.createdAt) }}</span>
                <span v-if="item.current" class="acc-history__current">Sesión actual</span>
              </li>
            </ul>
            <div v-else class="acc-note">Aún no hay accesos registrados.</div>

            <button class="acc-danger-link" @click="confirmLogoutAll = true">
              Cerrar sesión en todos los dispositivos
            </button>
          </div>
        </section>

        <!-- Sección 3 — Notificaciones personales -->
        <section class="acc-section">
          <div class="acc-section__label">Notificaciones</div>

          <div class="acc-subtitle">Canales</div>
          <div class="acc-toggle-list">
            <label v-for="channel in CHANNELS" :key="channel.key" class="acc-toggle-row">
              <span class="acc-toggle-row__text">
                <span class="acc-toggle-row__title">{{ channel.label }}</span>
                <span class="acc-toggle-row__hint">{{ channel.hint }}</span>
              </span>
              <input v-model="prefs[channel.key]" type="checkbox" class="acc-switch" />
            </label>
          </div>

          <div class="acc-subtitle">Frecuencia</div>
          <div class="acc-pills">
            <button
              v-for="option in FREQUENCY_OPTIONS"
              :key="option.value"
              type="button"
              class="acc-pill"
              :class="{ 'acc-pill--active': prefs.frequency === option.value }"
              @click="prefs.frequency = option.value"
            >
              {{ option.label }}
            </button>
          </div>

          <div class="acc-subtitle">No molestar</div>
          <label class="acc-toggle-row">
            <span class="acc-toggle-row__text">
              <span class="acc-toggle-row__title">Silenciar notificaciones en un horario</span>
              <span class="acc-toggle-row__hint">Fuera de este horario se avisa con normalidad.</span>
            </span>
            <input v-model="prefs.dndEnabled" type="checkbox" class="acc-switch" />
          </label>
          <div v-if="prefs.dndEnabled" class="acc-dnd">
            <label class="acc-dnd__field">
              <span class="acc-field__label">Desde</span>
              <input v-model="prefs.dndStart" type="time" class="acc-input acc-dnd__input" />
            </label>
            <label class="acc-dnd__field">
              <span class="acc-field__label">Hasta</span>
              <input v-model="prefs.dndEnd" type="time" class="acc-input acc-dnd__input" />
            </label>
          </div>

          <div v-if="prefsError" class="acc-error">{{ prefsError }}</div>
          <button class="acc-btn acc-btn--spaced" :disabled="!prefsDirty || savingPrefs || !prefsValid" @click="onSavePrefs">
            Guardar preferencias
          </button>
        </section>

        <!-- Sección — Datos de trabajo (solo técnicos vinculados) -->
        <section v-if="isTechnician" class="acc-section">
          <div class="acc-section__label">Datos de trabajo</div>

          <label class="acc-field acc-field--narrow">
            <span class="acc-field__label">Teléfono de contacto</span>
            <input v-model="workForm.phone" type="tel" class="acc-input" placeholder="Ej. 5512345678" />
            <span class="acc-field__hint">Número con el que el parque puede localizarte. Escríbelo igual que lo tiene registrado el parque.</span>
          </label>

          <label class="acc-toggle-row acc-toggle-row--card">
            <span class="acc-toggle-row__text">
              <span class="acc-toggle-row__title">Disponible para recibir tickets</span>
              <span class="acc-toggle-row__hint">Apágalo si estás de descanso o fuera de turno.</span>
            </span>
            <input v-model="workForm.acceptingTickets" type="checkbox" class="acc-switch" />
          </label>

          <div v-if="workError" class="acc-error">{{ workError }}</div>
          <button class="acc-btn acc-btn--spaced" :disabled="!workDirty || savingWork || !workForm.phone.trim()" @click="onSaveWork">
            Guardar datos de trabajo
          </button>
        </section>

        <!-- Sección 4 — Mi acceso (solo lectura) -->
        <section class="acc-section">
          <div class="acc-section__label">Mi acceso</div>

          <div class="acc-access-card">
            <div class="acc-access-row">
              <span class="acc-access-row__label">Rol</span>
              <span class="acc-role-pill" :style="{ backgroundColor: roleInfo.color }">{{ roleInfo.label }}</span>
            </div>
            <div class="acc-access-row">
              <span class="acc-access-row__label">Acceso desde</span>
              <span class="acc-access-row__value">{{ formatDate(user.activatedAt) }}</span>
            </div>
            <div class="acc-access-row">
              <span class="acc-access-row__label">Último acceso</span>
              <span class="acc-access-row__value">{{ formatDateTime(user.lastLoginAt) }}</span>
            </div>
          </div>

          <div v-if="user.role === 'CUSTOM' && customPagesLabel" class="acc-note acc-note--spaced">
            Páginas con acceso: {{ customPagesLabel }}
          </div>

          <router-link
            v-if="user.role === 'ADMIN'"
            :to="{ path: '/configuracion', query: { section: 'usuarios' } }"
            class="acc-link"
          >
            Gestionar usuarios del parque →
          </router-link>
        </section>
      </main>
    </div>

    <div v-if="confirmLogoutAll" class="acc-overlay" @click="confirmLogoutAll = false">
      <div class="acc-confirm" @click.stop>
        <p>
          Esto cerrará tu sesión en todos los dispositivos incluyendo este. Tendrás que iniciar sesión de nuevo.
        </p>
        <div class="acc-confirm__actions">
          <button class="acc-confirm__cancel" @click="confirmLogoutAll = false">Cancelar</button>
          <button class="acc-confirm__ok" :disabled="loggingOutAll" @click="onLogoutAll">Confirmar</button>
        </div>
      </div>
    </div>

    <div v-if="toast" class="acc-toast"><span class="mdi mdi-check-circle"></span> {{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/services/authStore'
import {
  updateProfile, changePassword, getLoginHistory,
  uploadAvatar, deleteAvatar, avatarSrc,
  getNotificationPreferences, saveNotificationPreferences, saveTechnicianWork,
} from '@/services/auth'

const router = useRouter()
const { state, refreshUser, logoutAll } = useAuthStore()
const user = computed(() => state.user)

const ROLE_INFO = {
  ADMIN: { label: 'Administrador general', color: '#3b82f6' },
  OPERATIONS: { label: 'Operativo', color: '#10b981' },
  FINANCE: { label: 'Financiero', color: '#8b5cf6' },
  MAINTENANCE_SUPERVISOR: { label: 'Supervisor de mantenimiento', color: '#f0a500' },
  TECHNICIAN: { label: 'Técnico', color: '#64748b' },
}

const roleInfo = computed(() => {
  const u = user.value
  if (!u) return { label: '', color: '#64748b' }
  if (u.role === 'CUSTOM') return { label: u.customRole?.name || 'Personalizado', color: '#64748b' }
  return ROLE_INFO[u.role] || { label: u.role, color: '#64748b' }
})

const initials = computed(() => {
  const source = user.value?.name || user.value?.email || ''
  return (
    source
      .split(' ')
      .filter(Boolean)
      .map((s) => s[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  )
})

const PAGE_LABELS = {
  dashboard: 'Dashboard',
  contratos: 'Contratos',
  finanzas: 'Finanzas',
  mantenimiento: 'Mantenimiento',
  servicios: 'Servicios',
  cobranza: 'Cobranza',
  configuracion: 'Configuración',
}
const customPagesLabel = computed(() => {
  const perms = user.value?.customRole?.permissions || {}
  return Object.keys(PAGE_LABELS)
    .filter((key) => perms[key]?.view)
    .map((key) => PAGE_LABELS[key])
    .join(', ')
})

function formatDate(value) {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
}

function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  const day = date.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
  const time = date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${day}, ${time}`
}

// --- Toast ---
const toast = ref('')
let toastTimer = null
function showToast(message) {
  toast.value = message
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value = ''
  }, 3000)
}

// --- Datos personales ---
const profileForm = reactive({ name: '', phone: '', jobTitle: '' })
const original = reactive({ name: '', phone: '', jobTitle: '' })
const savingProfile = ref(false)
const profileError = ref('')

function loadProfileForm() {
  const u = user.value
  if (!u) return
  const values = { name: u.name || '', phone: (u.phone || '').replace(/\D/g, ''), jobTitle: u.jobTitle || '' }
  Object.assign(profileForm, values)
  Object.assign(original, values)
}

// El campo guarda solo dígitos; se muestra con formato (55) 1234-5678.
function formatPhone(digits) {
  const d = digits.slice(0, 10)
  if (d.length <= 2) return d.length ? `(${d}` : ''
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
}

const formattedPhone = computed(() => formatPhone(profileForm.phone))

function onPhoneInput(event) {
  const digits = event.target.value.replace(/\D/g, '').slice(0, 10)
  profileForm.phone = digits
  // Si el valor ya era el mismo, Vue no re-renderiza; forzamos el formato en el input.
  event.target.value = formatPhone(digits)
}

const profileDirty = computed(
  () =>
    profileForm.name.trim() !== original.name ||
    profileForm.phone !== original.phone ||
    profileForm.jobTitle.trim() !== original.jobTitle
)

const PROFILE_ERRORS = {
  NAME_REQUIRED: 'El nombre es obligatorio.',
  INVALID_PHONE: 'El teléfono debe tener 10 dígitos.',
}

async function onSaveProfile() {
  profileError.value = ''
  savingProfile.value = true
  try {
    await updateProfile({
      name: profileForm.name,
      phone: profileForm.phone,
      jobTitle: profileForm.jobTitle,
    })
    await refreshUser()
    loadProfileForm()
    showToast('Datos personales actualizados')
  } catch (err) {
    profileError.value = PROFILE_ERRORS[err.message] || 'No se pudieron guardar los datos.'
  } finally {
    savingProfile.value = false
  }
}

// --- Seguridad ---
const pwForm = reactive({ current: '', next: '', confirm: '' })
const show = reactive({ current: false, next: false, confirm: false })
const savingPassword = ref(false)
const pwError = ref('')

const strength = computed(() => {
  const p = pwForm.next
  let score = 0
  if (p.length >= 8) score++
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++
  if (/\d/.test(p)) score++
  if (/[^A-Za-z0-9]/.test(p) || p.length >= 12) score++
  if (score <= 1) return { percent: 33, color: '#dc2626', label: 'Débil' }
  if (score <= 3) return { percent: 66, color: '#f0a500', label: 'Moderada' }
  return { percent: 100, color: '#10b981', label: 'Fuerte' }
})

const meetsRules = computed(() => pwForm.next.length >= 8 && /[A-Z]/.test(pwForm.next) && /\d/.test(pwForm.next))
const pwMatches = computed(() => !!pwForm.confirm && pwForm.confirm === pwForm.next)
const canChangePassword = computed(() => !!pwForm.current && meetsRules.value && pwMatches.value)

const PASSWORD_ERRORS = {
  WRONG_CURRENT_PASSWORD: 'La contraseña actual no es correcta.',
  WEAK_PASSWORD: 'La nueva contraseña debe tener al menos 8 caracteres, una mayúscula y un número.',
}

async function onChangePassword() {
  pwError.value = ''
  savingPassword.value = true
  try {
    await changePassword(pwForm.current, pwForm.next)
    pwForm.current = ''
    pwForm.next = ''
    pwForm.confirm = ''
    showToast('Contraseña actualizada. Se cerraron tus otras sesiones.')
  } catch (err) {
    pwError.value = PASSWORD_ERRORS[err.message] || 'No se pudo cambiar la contraseña.'
  } finally {
    savingPassword.value = false
  }
}

// --- Historial de accesos / cerrar sesiones ---
const history = ref([])
const confirmLogoutAll = ref(false)
const loggingOutAll = ref(false)

const DEVICE_INFO = {
  computer: { icon: 'mdi-laptop', label: 'Computadora' },
  mobile: { icon: 'mdi-cellphone', label: 'Móvil' },
  tablet: { icon: 'mdi-tablet', label: 'Tablet' },
}
const deviceIcon = (type) => (DEVICE_INFO[type] || DEVICE_INFO.computer).icon
const deviceLabel = (type) => (DEVICE_INFO[type] || DEVICE_INFO.computer).label

async function loadHistory() {
  try {
    history.value = await getLoginHistory()
  } catch {
    history.value = []
  }
}

async function onLogoutAll() {
  loggingOutAll.value = true
  try {
    await logoutAll()
    router.push('/login')
  } finally {
    loggingOutAll.value = false
    confirmLogoutAll.value = false
  }
}

// --- Foto de perfil ---
const avatarInput = ref(null)
const uploadingAvatar = ref(false)
const avatarError = ref('')
const avatarImg = computed(() => avatarSrc(user.value?.avatarUrl))

const AVATAR_ERRORS = {
  INVALID_IMAGE_TYPE: 'Usa una imagen JPG, PNG o WebP.',
  IMAGE_TOO_LARGE: 'La imagen no debe pasar de 2 MB.',
}

async function onAvatarSelected(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  avatarError.value = ''
  uploadingAvatar.value = true
  try {
    await uploadAvatar(file)
    await refreshUser()
    showToast('Foto de perfil actualizada')
  } catch (err) {
    avatarError.value = AVATAR_ERRORS[err.message] || 'No se pudo subir la foto.'
  } finally {
    uploadingAvatar.value = false
  }
}

async function onRemoveAvatar() {
  avatarError.value = ''
  uploadingAvatar.value = true
  try {
    await deleteAvatar()
    await refreshUser()
    showToast('Foto de perfil eliminada')
  } catch {
    avatarError.value = 'No se pudo quitar la foto.'
  } finally {
    uploadingAvatar.value = false
  }
}

// --- Notificaciones personales ---
const CHANNELS = [
  { key: 'inAppEnabled', label: 'En la aplicación', hint: 'Avisos dentro de Parkos.' },
  { key: 'emailEnabled', label: 'Correo electrónico', hint: 'Se envían a tu correo de acceso.' },
]
const FREQUENCY_OPTIONS = [
  { value: 'IMMEDIATE', label: 'Inmediato' },
  { value: 'DAILY', label: 'Resumen diario' },
  { value: 'WEEKLY', label: 'Resumen semanal' },
]
const prefs = reactive({
  emailEnabled: true, whatsappEnabled: false, inAppEnabled: true,
  frequency: 'IMMEDIATE', dndEnabled: false, dndStart: '22:00', dndEnd: '07:00',
})
const prefsOriginal = reactive({ ...prefs })
const savingPrefs = ref(false)
const prefsError = ref('')

const prefsDirty = computed(() => Object.keys(prefs).some((k) => prefs[k] !== prefsOriginal[k]))
const prefsValid = computed(() => !prefs.dndEnabled || (!!prefs.dndStart && !!prefs.dndEnd))

async function loadPrefs() {
  try {
    const data = await getNotificationPreferences()
    Object.assign(prefs, data)
    Object.assign(prefsOriginal, data)
  } catch {
    prefsError.value = 'No se pudieron cargar tus preferencias.'
  }
}

async function onSavePrefs() {
  prefsError.value = ''
  savingPrefs.value = true
  try {
    const saved = await saveNotificationPreferences({ ...prefs })
    Object.assign(prefs, saved)
    Object.assign(prefsOriginal, saved)
    showToast('Preferencias de notificación guardadas')
  } catch {
    prefsError.value = 'No se pudieron guardar las preferencias.'
  } finally {
    savingPrefs.value = false
  }
}

// --- Datos de trabajo (técnico) ---
const isTechnician = computed(() => user.value?.role === 'TECHNICIAN' && !!user.value?.technician)
const workForm = reactive({ phone: '', acceptingTickets: true })
const workOriginal = reactive({ phone: '', acceptingTickets: true })
const savingWork = ref(false)
const workError = ref('')

const workDirty = computed(
  () => workForm.phone.trim() !== workOriginal.phone || workForm.acceptingTickets !== workOriginal.acceptingTickets
)

function loadWorkForm() {
  const t = user.value?.technician
  if (!t) return
  const values = { phone: t.phone || '', acceptingTickets: t.acceptingTickets !== false }
  Object.assign(workForm, values)
  Object.assign(workOriginal, values)
}

const WORK_ERRORS = { PHONE_REQUIRED: 'El teléfono de contacto es obligatorio.' }

async function onSaveWork() {
  workError.value = ''
  savingWork.value = true
  try {
    await saveTechnicianWork({ phone: workForm.phone, acceptingTickets: workForm.acceptingTickets })
    await refreshUser()
    loadWorkForm()
    showToast('Datos de trabajo actualizados')
  } catch (err) {
    workError.value = WORK_ERRORS[err.message] || 'No se pudieron guardar los datos.'
  } finally {
    savingWork.value = false
  }
}

onMounted(() => {
  loadProfileForm()
  loadWorkForm()
  loadHistory()
  loadPrefs()
})
watch(user, (value, old) => {
  // Primera carga del usuario (p. ej. recarga directa de la página).
  if (value && !old) {
    loadProfileForm()
    loadWorkForm()
  }
})
</script>

<style scoped>
.acc-view { display: flex; flex-direction: column; flex: 1; min-height: 0; }

.acc-topbar {
  display: flex;
  align-items: center;
  padding: 0.6rem 1.5rem;
  background: var(--side-panel);
  border-left: 1px solid var(--border-subtle);
  height: 56px;
  box-sizing: border-box;
}
.acc-topbar__section { font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #ffffff; }
.acc-topbar__spacer { flex: 1; }
.acc-topbar__avatar {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font: 600 11px Inter, sans-serif;
}

.acc-loading { flex: 1; display: flex; align-items: center; justify-content: center; color: #64748b; }
.acc-loading__spinner { font-size: 1.8rem; animation: acc-spin 0.8s linear infinite; }
@keyframes acc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.acc-body { display: flex; flex: 1; min-height: 0; background: #ffffff; }

.acc-identity {
  width: 280px;
  flex-shrink: 0;
  border-right: 1px solid #f1f5f9;
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
}
.acc-identity__avatar {
  width: 88px; height: 88px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-family: var(--font-display); font-weight: 700; font-size: 28px;
  margin-bottom: 10px;
}
.acc-identity__name { font-family: var(--font-display); font-weight: 700; font-size: 16px; color: #1e3a5f; }
.acc-identity__role { font: 400 12px Inter, sans-serif; }
.acc-identity__park { font: 400 11px Inter, sans-serif; color: #94a3b8; display: flex; align-items: center; gap: 4px; margin-top: 4px; }
.acc-identity__park .mdi { font-size: 12px; }
.acc-identity__since { font: 400 10px Inter, sans-serif; color: #94a3b8; margin-top: auto; padding-top: 24px; }

.acc-content { flex: 1; min-width: 0; overflow-y: auto; padding: 24px 32px; }
.acc-content::-webkit-scrollbar { width: 6px; }
.acc-content::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 3px; }
.acc-content::-webkit-scrollbar-track { background: transparent; }

.acc-section { margin-bottom: 36px; max-width: 760px; }
.acc-section__label {
  font: 500 12px Inter, sans-serif; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px;
  border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; margin-bottom: 16px;
}

.acc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 24px; }
.acc-col { display: flex; flex-direction: column; }

.acc-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.acc-field__label { font: 500 11px Inter, sans-serif; color: #64748b; display: flex; align-items: center; gap: 8px; }
.acc-field__hint { font: 400 10px Inter, sans-serif; color: #94a3b8; }

.acc-input {
  width: 100%; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px;
  font: 400 13px Inter, sans-serif; color: #334155; background: #fff; box-sizing: border-box;
}
.acc-input:focus { outline: none; border-color: #3b82f6; }
.acc-input--readonly { background: #f8f9fc; color: #64748b; }

.acc-badge { font: 500 10px Inter, sans-serif; padding: 1px 8px; border-radius: 10px; }
.acc-badge--ok { background: #d1fae5; color: #047857; }

.acc-phone { display: flex; align-items: center; gap: 6px; }
.acc-phone__prefix { font: 500 13px Inter, sans-serif; color: #64748b; }
.acc-phone__input { flex: 1; }

.acc-password { max-width: 360px; }
.acc-input-wrap { position: relative; display: flex; align-items: center; }
.acc-input-wrap .acc-input { padding-right: 34px; }
.acc-eye { position: absolute; right: 8px; background: none; border: none; cursor: pointer; color: #94a3b8; font-size: 16px; display: flex; }
.acc-eye:hover { color: #334155; }
.acc-match { position: absolute; right: 32px; color: #10b981; font-size: 16px; }

.acc-strength { display: flex; align-items: center; gap: 8px; }
.acc-strength__bar { flex: 1; height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden; }
.acc-strength__fill { height: 100%; border-radius: 2px; transition: width 0.15s, background-color 0.15s; }
.acc-strength__label { font: 500 11px Inter, sans-serif; min-width: 60px; text-align: right; }

.acc-note { font: 400 10px Inter, sans-serif; color: #94a3b8; margin-bottom: 14px; }
.acc-note--spaced { margin: 10px 0 0; }
.acc-error { font: 400 11px Inter, sans-serif; color: #dc2626; margin-bottom: 8px; }

.acc-btn {
  padding: 8px 16px; background: #3b82f6; color: #fff; border: none; border-radius: 6px;
  font: 500 13px Inter, sans-serif; cursor: pointer;
}
.acc-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.acc-access-card {
  background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.acc-access-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.acc-access-row__label { font: 400 10px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px; }
.acc-access-row__value { font: 500 13px Inter, sans-serif; color: #1e3a5f; }
.acc-role-pill { font: 500 11px Inter, sans-serif; padding: 3px 10px; border-radius: 12px; color: #fff; }

.acc-subsection { margin-top: 28px; padding-top: 16px; border-top: 1px solid #f1f5f9; max-width: 560px; }
.acc-subsection__label { font: 500 11px Inter, sans-serif; color: #64748b; margin-bottom: 8px; }
.acc-history { list-style: none; margin: 0 0 12px; padding: 0; }
.acc-history__row { display: flex; align-items: center; gap: 10px; height: 36px; border-bottom: 1px solid #f8fafc; }
.acc-history__icon { font-size: 14px; color: #94a3b8; }
.acc-history__device { font: 400 12px Inter, sans-serif; color: #1e3a5f; flex: 1; min-width: 0; }
.acc-history__date { font: 400 11px Inter, sans-serif; color: #94a3b8; white-space: nowrap; }
.acc-history__current { font: 500 10px Inter, sans-serif; color: #047857; background: #d1fae5; padding: 1px 8px; border-radius: 10px; white-space: nowrap; }
.acc-danger-link { background: none; border: none; padding: 0; cursor: pointer; font: 400 12px Inter, sans-serif; color: #dc2626; }
.acc-danger-link:hover { text-decoration: underline; }

.acc-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.acc-confirm { background: #fff; border-radius: 8px; padding: 20px; width: 340px; font: 400 13px Inter, sans-serif; color: #334155; }
.acc-confirm p { margin: 0; }
.acc-confirm__actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.acc-confirm__cancel { background: none; border: 1px solid #e2e8f0; border-radius: 6px; padding: 6px 12px; cursor: pointer; font: 400 12px Inter, sans-serif; color: #334155; }
.acc-confirm__ok { background: #dc2626; color: #fff; border: none; border-radius: 6px; padding: 6px 12px; cursor: pointer; font: 500 12px Inter, sans-serif; }
.acc-confirm__ok:disabled { opacity: 0.6; cursor: not-allowed; }

.acc-link { display: inline-block; margin-top: 12px; font: 500 12px Inter, sans-serif; color: #3b82f6; text-decoration: none; }
.acc-link:hover { text-decoration: underline; }

.acc-toast {
  position: fixed; top: 20px; right: 20px; background: #10b981; color: #fff; padding: 10px 16px;
  border-radius: 6px; font: 500 13px Inter, sans-serif; display: flex; align-items: center; gap: 6px;
  z-index: 3000; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.acc-hidden { display: none; }
.acc-avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block; }
.acc-topbar__avatar, .acc-identity__avatar { overflow: hidden; }
.acc-identity__photo-actions { display: flex; gap: 12px; }
.acc-photo-link { background: none; border: none; padding: 0; cursor: pointer; font: 500 11px Inter, sans-serif; color: #3b82f6; }
.acc-photo-link:hover { text-decoration: underline; }
.acc-photo-link--danger { color: #dc2626; }
.acc-photo-link:disabled { opacity: 0.5; cursor: not-allowed; }
.acc-error--center { text-align: center; margin: 4px 0 0; }

.acc-subtitle { font: 500 11px Inter, sans-serif; color: #64748b; margin: 18px 0 8px; }
.acc-subtitle:first-of-type { margin-top: 0; }
.acc-toggle-list { display: flex; flex-direction: column; }
.acc-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 8px 0; max-width: 480px; cursor: pointer; }
.acc-toggle-row--card { background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; margin-bottom: 16px; }
.acc-toggle-row__text { display: flex; flex-direction: column; gap: 2px; }
.acc-toggle-row__title { font: 500 13px Inter, sans-serif; color: #1e3a5f; }
.acc-toggle-row__hint { font: 400 10px Inter, sans-serif; color: #94a3b8; }

.acc-switch {
  appearance: none; -webkit-appearance: none; flex-shrink: 0;
  width: 34px; height: 20px; border-radius: 10px; background: #cbd5e1; position: relative;
  cursor: pointer; transition: background 0.15s; border: none; margin: 0;
}
.acc-switch::after {
  content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%;
  background: #fff; transition: transform 0.15s;
}
.acc-switch:checked { background: #3b82f6; }
.acc-switch:checked::after { transform: translateX(14px); }

.acc-pills { display: flex; flex-wrap: wrap; gap: 8px; }
.acc-pill {
  padding: 5px 14px; border-radius: 16px; border: 1px solid #e2e8f0; background: #fff; cursor: pointer;
  font: 400 12px Inter, sans-serif; color: #64748b;
}
.acc-pill:hover { border-color: #94a3b8; }
.acc-pill--active { background: #3b82f6; border-color: #3b82f6; color: #fff; }

.acc-dnd { display: flex; gap: 16px; margin: 8px 0 4px; }
.acc-dnd__field { display: flex; flex-direction: column; gap: 6px; }
.acc-dnd__input { width: 120px; }

.acc-field--narrow { max-width: 360px; }
.acc-btn--spaced { margin-top: 16px; }

@media (max-width: 900px) {
  .acc-body { flex-direction: column; overflow-y: auto; }
  .acc-identity { width: 100%; border-right: none; border-bottom: 1px solid #f1f5f9; padding: 20px; }
  .acc-identity__since { margin-top: 8px; padding-top: 0; }
  .acc-content { overflow: visible; padding: 20px; }
  .acc-grid { grid-template-columns: 1fr; }
}
</style>